import
  dayjs
from 'dayjs';

import {
  Sentry
} from '@vodacom/web-sentry-react';

import {
  JourneyProvider
} from '@vodacom/web-common';

import {
  deeplink,
  navigateToMiniProgram,
  openBrowser
} from '@vodacom/mini-program-bridge';

import {
  getStorageWithExpiry,
  setStorageWithExpiry
} from './storage-with-expiry';

import {
  CacheKeys,
  JavascriptTypes,
  OriginContexts,
  PaymentOptions,
  SentryEventTypes
} from '../constants';

const urlPattern = new RegExp(/^((http|https|mailto|tel):\/\/)/);

export const toKebabCase = (str) => str && str
    .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    .map(x => x.toLowerCase())
    .join('-');

export const isExternalUrl = (url) => {

  return urlPattern.test(url);
};

export const isValidEncMsisdn = encMsisdn => {

  return encMsisdn?.length === 512;
};

export const getAuthToken = () => {

  return getStorageWithExpiry('Auth', 'token');
};

export const formatDate = (oldDate, offset = 0) => {

  return dayjs(oldDate).add(offset, 'day').format('DD MMM YYYY');
};

export const printCurrency = (value) => {

  const amount = parseFloat(value);

  return !isNaN(amount) ? `R${amount.toFixed(2)}` : '';
};

export const extractDays = (days) => {
  return parseInt(days.match(/\d+/g));
};

export const createGrandTotal = (
  retailPrice,
  voucherValue,
  paymentOption
) => {

  const freeAmount = voucherValue - retailPrice;

  if (!paymentOption || paymentOption === PaymentOptions.Advance.value || freeAmount === 0) {
    return printCurrency(retailPrice);
  }

  return `${printCurrency(retailPrice)} + Extra ${printCurrency(freeAmount)} free`;
};

export const formatVoucherCode = (voucherCode) => {

  return voucherCode?.match(/.{1,3}/g)?.join(' ');
};

export const formatCurrency = (
  amount,
  currencyPrefix = 'R',
  fixedDecimals = 2
) => {

  amount = Number(amount);

  if (isNaN(amount)) {
    return amount;
  }

  return `${ currencyPrefix }${ amount?.toFixed(fixedDecimals)?.replace(/[.,]00$/, '') }`;
};

export const isAdvanceEligible = (eligibility, advanceValue) => {

  if (!eligibility || !eligibility.eligibility) {
    return false;
  }

  return eligibility.maxEligibleAmount >= advanceValue;
};

export const isOfferIdEligible = (id, eligibility) => {

  if (!id || !Array.isArray(eligibility?.offers)) {
    return false;
  }

  for (const offer of eligibility?.offers) {

    if (offer?.offerId === id) {
      return true;
    }
  }

  return false;
};

export const generateDynamicMessage = (
  message,
  replaceObject
) => {

  if (!message) {
    return message;
  }

  for (const [ key, value ] of Object.entries(replaceObject)) {

    if (!key) {
      continue;
    }

    message = message.replace(key, value);
  }

  return message;
};

const getType = (obj) => {
  if (typeof obj === JavascriptTypes.Object) {

    return Object.prototype.toString.call(obj).slice(8, -1).toLowerCase();
  }

  return typeof obj;
};

const areArraysEqual = (left, right) => {

  if (left.length !== right.length) {

    return false;
  }

  for (let i = 0; i < left.length; i ++) {

    if (!isEqual(left[i], right[i])) {

      return false;
    }
  }

  return true;
};

const areObjectsEqual = (left, right) => {

  if (Object.keys(left).length !== Object.keys(right).length) {

    return false;
  }

  for (let key in left) {

    if (Object.prototype.hasOwnProperty.call(left, key)) {

      if (!isEqual(left[key], right[key])) {

        return false;
      }
    }
  }

  return true;
};

const areFunctionsEqual = (left, right) => {
  return left.toString() === right.toString();
};

const arePrimitivesEqual = (left, right) => {

  if (getType(left) === JavascriptTypes.String) {

    return left.toUpperCase() === right.toUpperCase();
  }

  return left === right;
};

export const isEqual = (left, right) => {

	const type = getType(left);

	if (type !== getType(right)) {

    return false;
  }

  switch (type) {
    case JavascriptTypes.Array:
      return areArraysEqual(left, right);

    case JavascriptTypes.Object:
      return areObjectsEqual(left, right);

    case JavascriptTypes.Function:
      return areFunctionsEqual(left, right);

    default:
      return arePrimitivesEqual(left, right);
  }
};

const convertToKilobytes = (dataBalance) => {
  const units = [ 'KB', 'MB', 'GB', 'TB', 'PB' ];

  for (let index = 0; index < units.length; index ++) {

    const unit = units[index];

    if (dataBalance?.includes(unit)) {

      return parseFloat(dataBalance?.replace(/[^0-9.-]+/g, '')) * 1024 ** index;
    }
  }
};

export const hasInsufficientBalance = (
  dataString = '0KB',
  airtimeString = 'R 0.00',
  minDataBalance = 5120,
  minAirtimeBalance = 500
) => {

  const dataBalance = convertToKilobytes(dataString);
  const airtimeBalance = parseFloat(airtimeString?.replace(/[^0-9.-]+/g, '')) * 100;

  return dataBalance < minDataBalance && airtimeBalance < minAirtimeBalance;
};

export const handleRedirect = (history, url, isZeroRated) => {

  setStorageWithExpiry('Prompt', 'returnUrl', history.location);

  history.push({
    pathname: '/handle-redirect',
    state: {
      url,
      isZeroRated
    }
  });
};

export const handleSentryEvent = (
  eventType,
  data
) => {

  if (!eventType || !data) {
    return;
  }

  switch (eventType) {
    case SentryEventTypes.Error:

      Sentry.error({
        journey: data.journey,
        component: data.component,
        error: new Error(data?.message || 'Unknown Mini-app error'),
        ...data
      });
      return;

    case SentryEventTypes.Info:

      Sentry.info({
        journey: data.journey,
        component: data.component,
        message: data.message
      });
      return;

    case SentryEventTypes.Warning:

      Sentry.warn({
        journey: data.journey,
        component: data.component,
        message: data.message
      });
      return;

    default:
      return;
  }
};

export const handleUtagLinkEvent = (linkData) => {

  const context = JourneyProvider.get(CacheKeys.ConfigContext, 'value');

  window?.utag?.link({
    ...linkData,
    ...isEqual(context, OriginContexts.VodaPay) && { header_type: 'Mini-App' }
  });
};

export const handleUtagViewEvent = (viewData) => {
  const context = JourneyProvider.get(CacheKeys.ConfigContext, 'value');

  window?.utag?.view({
    ...viewData,
    ...(isEqual(context, OriginContexts.VodaPay) && { header_type: 'Mini-App' })
  });
};

export const openDeeplink = (appId) => {

  if (!window?.my) {
    return;
  }

  appId.startsWith('vodapaywallet')
    ?  deeplink(appId)
    : navigateToMiniProgram(appId);
};

export const openWebView = (url) => {

  if (!window?.my) {
    return;
  }

  openBrowser(url);

};

export const hasAppId = (config, appName) => {
  return config?.miniApps?.flatMap(miniApp => miniApp.miniAppName).includes(appName);
};

export const getAppId = (config, appName) => {
  return config?.miniApps?.filter(miniApp => miniApp.miniAppName === appName)[0].miniAppId;
};

export const  capitalizeFirstLetter = (string = '') => {

  if (!string) {
    return string;
  }

  return `${string.charAt(0).toUpperCase()}${string.slice(1)}`;
};

export const debounce = (func, timeoutInMilliSeconds = 5000) => {

  let timer;
  return (...args) => {

    if (!timer) {
      func(...args); // Immediately invoke the function
    }
    clearTimeout(timer);
    timer = setTimeout(() => {

      timer = undefined;
    }, timeoutInMilliSeconds);
  };
};
